// ── V162: 중국어 → 한글 발음 (병음을 한글로) ─────────────────────────────────────
// 사장님: "한국어로 번역이 안 되면 그냥 병음을 한국어로 적어 주면 될 것 같아"
//   红盛绿槽竹碳 → 훙성뤼차오주탄 · 上海青 → 상하이칭 · 老干妈 → 라오간마
// 표기는 국립국어원 중국어 표기(주음 부호와 한글 대조표)를 따른다: zh·j → ㅈ, z → ㅉ, s → ㅆ,
//   ㅈ·ㅊ 뒤의 이중 모음은 단모음(jia 자, jiu 주), zi·zhi·si·shi → 쯔·즈·쓰·스, ü → 위(ju 쥐, yu 위)
// 병음은 pinyin-pro(이미 발음 표시에 쓰는 것)로 만든다. 숫자·영문·한글은 그대로 둔다.

const CHO = { '': 11, b: 7, p: 17, m: 6, f: 17, d: 3, t: 16, n: 2, l: 5, g: 0, k: 15, h: 18, j: 12, q: 14, x: 9, zh: 12, ch: 14, sh: 9, r: 5, z: 13, c: 14, s: 10 };
const V = { a: 0, ae: 1, ya: 2, eo: 4, e: 5, yeo: 6, ye: 7, o: 8, wa: 9, oe: 11, yo: 12, u: 13, wo: 14, we: 15, wi: 16, yu: 17, eu: 18, i: 20 };
const JONG = { '': 0, n: 4, l: 8, ng: 21 };
// 운모 → 한글 음절들 [[모음, 받침], …]
const FIN = {
  a: [['a']], o: [['o']], e: [['eo']], ê: [['e']], ai: [['a'], ['i']], ei: [['e'], ['i']], ao: [['a'], ['o']], ou: [['eo'], ['u']],
  an: [['a', 'n']], en: [['eo', 'n']], ang: [['a', 'ng']], eng: [['eo', 'ng']], er: [['eo', 'l']], ong: [['u', 'ng']],
  i: [['i']], ia: [['ya']], ie: [['ye']], iao: [['ya'], ['o']], iu: [['yu']], ian: [['ye', 'n']], in: [['i', 'n']],
  iang: [['ya', 'ng']], ing: [['i', 'ng']], iong: [['yu', 'ng']],
  u: [['u']], ua: [['wa']], uo: [['wo']], uai: [['wa'], ['i']], ui: [['u'], ['i']], uan: [['wa', 'n']], un: [['u', 'n']],
  uang: [['wa', 'ng']], ueng: [['wo', 'ng']],
  ü: [['wi']], üe: [['we']], üan: [['wi'], ['a', 'n']], ün: [['wi', 'n']],
};
// 성모 없이 y·w로 시작하는 음절 → 운모
const ZERO = {
  yi: 'i', ya: 'ia', yo: 'o', ye: 'ie', yai: 'ai', yao: 'iao', you: 'iu', yan: 'ian', yin: 'in', yang: 'iang', ying: 'ing', yong: 'iong',
  yu: 'ü', yue: 'üe', yuan: 'üan', yun: 'ün',
  wu: 'u', wa: 'ua', wo: 'uo', wai: 'uai', wei: 'wei', wan: 'uan', wen: 'wen', wang: 'uang', weng: 'ueng',
};
const ZERO_SPECIAL = { wei: [['we'], ['i']], wen: [['wo', 'n']], o: [['o']] };
const PALATAL = { ya: 'a', yeo: 'eo', ye: 'e', yo: 'o', yu: 'u' };

const block = (cho, v, jong = '') => String.fromCharCode(0xAC00 + (cho * 21 + V[v]) * 28 + JONG[jong]);

/** 병음 한 음절(성조 없음) → 한글. 모르는 모양이면 '' */
export function syllableToHangul(py) {
  let s = String(py || '').toLowerCase().trim().replace(/v/g, 'ü').replace(/u:/g, 'ü');
  if (!s) return '';
  if (s === 'm' || s === 'n' || s === 'ng') return s === 'm' ? '음' : '은';
  let ini = '';
  let fin = s;
  const m = s.match(/^(zh|ch|sh|[bpmfdtnlgkhjqxrzcs])(.*)$/);
  if (m) { ini = m[1]; fin = m[2]; }
  let parts;
  if (!ini) {
    if (ZERO_SPECIAL[s]) parts = ZERO_SPECIAL[s];
    else if (ZERO[s]) parts = ZERO_SPECIAL[ZERO[s]] || FIN[ZERO[s]];
    else parts = FIN[s];
  } else {
    if (['j', 'q', 'x'].includes(ini)) fin = fin.replace(/^u/, 'ü');                     // ju = jü
    if (fin === 'i' && ['zh', 'ch', 'sh', 'r', 'z', 'c', 's'].includes(ini)) parts = [['eu']];   // zhi 즈 · si 쓰
    else if ((ini === 'l' || ini === 'n') && fin === 'ue') parts = FIN['üe'];
    else parts = FIN[fin];
  }
  if (!parts) return '';
  const cho = CHO[ini];
  return parts.map(([v, jong], i) => {
    let vv = v;
    if (i === 0 && ['j', 'q', 'zh', 'ch'].includes(ini) && PALATAL[vv]) vv = PALATAL[vv];   // jia 자 · qiu 추
    return block(i === 0 ? cho : 11, vv, jong || '');
  }).join('');
}

const HAN_RUN = /[㐀-鿿]+/g;

/**
 * 중국어 글 → 한글 발음. pinyinFn = pinyin-pro의 pinyin (테스트에서 넣을 수 있게)
 * 한자 덩어리마다 붙여 쓰고(红盛绿槽竹碳 → 훙성뤼차오주탄), 나머지 글자는 그대로
 */
export function zhToHangulWith(pinyinFn, text) {
  const src = String(text || '');
  if (!pinyinFn || !/[㐀-鿿]/.test(src)) return '';
  return src.replace(HAN_RUN, (run) => {
    let arr = [];
    try { arr = pinyinFn(run, { toneType: 'none', type: 'array' }) || []; } catch (e) { arr = []; }
    return arr.map(p => syllableToHangul(p) || p).join('');
  }).replace(/\s+/g, ' ').trim();
}

let pyFn = null;
/** 중국어 → 한글 발음 (병음 사전을 불러와서). 실패하면 '' */
export async function zhToHangul(text) {
  try {
    if (!pyFn) pyFn = (await import('pinyin-pro')).pinyin;
  } catch (e) { return ''; }
  const out = zhToHangulWith(pyFn, text);
  return /[가-힣]/.test(out) ? out : '';
}
